<template>
	<Application v-model:error-title="errorTitle" class="TradeHubMyOrg common-form"
		:loading="loading" :error-detail="errorDetail"
		:style="{ 'padding-left': isSideBarFixed ? '315px' : '15px' }">
		<template #navbar>
			<span style="font-size: 17px; font-weight: 600; line-height: 22px; padding-left: 24px;">{{
				$t('text.myOrg') }}</span>
		</template>
		<SideBar class="common-sidebar" :navigation="sections" :preselectedActiveLink="activeLink"
			:class="{ mobileSidebar: isMobile }" />

		<div class="content-container">
			<div class="my-org-container">
				<div class="my-org-container__header">
					<h1>{{ $t('text.myUsers') }}</h1>
				</div>

				<div class="card">
					<div class="card__header">
						<div class="card__title">
							<p class="title">{{ $t('text.myUsers') }}</p>
							<p class="subtitle">{{ $t('text.createUserAccountsAndManageRoles') }}</p>
						</div>
						<v-btn color="#00AEEF" variant="flat" prepend-icon="mdi-plus" @click="openAddUser" data-cy="add-user-button">
							{{ $t('text.addUser') }}
						</v-btn>
					</div>

					<SectionTable
						:items="userItems"
						:hideFooter="true"
						:columns="[
							{ id: 'email', width: '280px' },
							{ id: 'firstName', width: '160px' },
							{ id: 'lastName', width: '160px' },
							{ id: 'language', width: '140px' },
							{ id: 'userRole', type: 'slot', width: '120px' },
							{ id: 'status', type: 'slot', width: '140px', headerInfo: 'invitationStatusHeaderInfo' },
							{ id: 'actions', type: 'slot', width: '50px' }
						]"
						no-data-text="noData"
					>
						<template #column-userRole="{ item }">
							<v-chip :color="roleChipColor(item.userRole)" text-color="white" size="small">{{ item.userRole }}</v-chip>
						</template>
						<template #column-status="{ item }">
							<v-chip :color="statusChipColor(item.status)" text-color="white" size="small">{{ statusDisplayText(item.status) }}</v-chip>
						</template>
						<template #column-actions="{ item }">
							<PopoverMenu
								v-if="getOptionsForItem(item).length > 0"
								:items="getOptionsForItem(item)"
								@click="(action) => handleOptionClick(action, item)"
								data-cy="user-actions"
							/>
							<v-btn
								v-else
								class="dotsIcon"
								variant="plain"
								size="large"
								density="compact"
								icon="mdi-dots-horizontal-circle-outline"
								color="#9E9E9E"
								disabled
								data-cy="user-actions-disabled"
							/>
						</template>
					</SectionTable>
				</div>
			</div>
		</div>
	</Application>

	<!-- Add / Edit User Dialog (invitation) -->
	<AddUserDialog
		v-if="serviceProviderId"
		ref="userDialog"
		:mode="dialogMode"
		:user="dialogUser"
		:service-provider-id="serviceProviderId"
		@user-saved="handleUserSaved"
		@user-updated="handleUserUpdated"
	/>

	<!-- Delete confirmation -->
	<ConfirmationDialog
		v-model:show="showDelete"
		:title="deleteDialogTitle"
		:heading="deleteDialogTitle"
		:message="deleteDialogMessage"
		:confirm-button-text="$t('text.confirm')"
		:cancel-button-text="$t('text.cancel')"
		:confirm-button-color="deleteMode === 'user' ? 'error' : 'primary'"
		@confirm="confirmDelete"
	/>

	<!-- Ownership Transfer confirmation -->
	<ConfirmationDialog
		v-model:show="showOwnershipTransfer"
		:title="$t('text.transferOwnership')"
		:heading="$t('text.transferOwnership')"
		:message="$t('text.transferOwnershipConfirmMessage', { 
			name: `${transferTargetUser?.firstName || ''} ${transferTargetUser?.lastName || ''}`.trim() || transferTargetUser?.email,
			email: transferTargetUser?.email 
		})"
		:confirm-button-text="$t('text.transferOwnership')"
		:cancel-button-text="$t('text.cancel')"
		confirm-button-color="primary"
		@confirm="confirmTransferOwnership"
	/>

	<!-- Change Role confirmation -->
	<ConfirmationDialog
		v-model:show="showChangeRole"
		:title="$t('text.changeRole')"
		:heading="$t('text.changeRole')"
		:message="$t('text.changeRoleConfirmMessage', { 
			name: `${roleChangeTargetUser?.firstName || ''} ${roleChangeTargetUser?.lastName || ''}`.trim() || roleChangeTargetUser?.email,
			currentRole: roleChangeTargetUser?.userRole,
			newRole: targetRole
		})"
		:confirm-button-text="$t('text.confirm')"
		:cancel-button-text="$t('text.cancel')"
		confirm-button-color="primary"
		@confirm="confirmChangeRole"
	/>
</template>

<script>
import Common from '@/mixins/Common.vue'
import SideBar from '@/components/common/SideBar.vue'
import Application from '../Application.vue'
import SectionTable from '../packageDesigner/SectionTable.vue'
import AddUserDialog from './AddUserDialog.vue'
import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue'
import PopoverMenu from '@/components/common/PopoverMenu.vue'
import Toast from '@/mixins/Toast.vue'
import InvitationExecutive from '../../../../../api2/src/modules/trade-hub/InvitationExecutive'
import TourOperatorExecutive from '../../../../../api2/src/modules/trade-hub/TourOperatorExecutive'
import OwnershipTransferExecutive from '../../../../../api2/src/modules/trade-hub/OwnershipTransferExecutive'
import { InvitationRole, UserType } from '../../../../../shared/types/account-types'

export default {
	name: 'TradeHubSettingsView',
	components: { SideBar, Application, SectionTable, AddUserDialog, ConfirmationDialog, PopoverMenu },
	mixins: [Common, Toast],
			data() {
			return {
				errorTitle: null,
				errorDetail: null,
				sections: [{ id: 'myOrg', state: 'valid' }],
				activeLink: 'myOrg',
				loading: false,
				// How many overlapping beginLoading()/endLoading() calls are in flight - see
				// those methods for why this exists (MYS-7293 follow-up).
				loadingDepth: 0,
				users: [],
				invitations: [], // Store invitation data from backend
				dialogUser: { firstName: '', lastName: '', email: '', language: 'en', userRole: 'User' },
				dialogMode: 'add',
				showDelete: false,
				userToDelete: null,
				deleteMode: 'invitation', // 'invitation' or 'user'
				windowWidth: window.innerWidth,
				serviceProviderId: null,
				statusUpdateInterval: null, // For periodic status updates
				showOwnershipTransfer: false,
				transferTargetUser: null,
				showChangeRole: false,
				roleChangeTargetUser: null,
				targetRole: null,
			}
		},
	computed: {
		isMobile() {
			return this.windowWidth < 768
		},
		locale() {
			return this.$store.state.serviceLocale ?? 'de'
		},
		clientId() {
			return this.$store.state.selectedClient.sys.id
		},
		userItems() {
			// Combine invitations with users data for display
			return this.invitations.map(invitation => ({
				id: invitation.id,
				email: invitation.email,
				firstName: invitation.firstName || '',
				lastName: invitation.lastName || '',
				language: this.localeToLanguageLabel(invitation.preferredLocale),
				preferredLocale: invitation.preferredLocale, // Keep original locale for edit
				userRole: invitation.role,
				status: this.getComputedStatus(invitation), // Compute status client-side
				invitationId: invitation.id,
				expiresAt: invitation.expiresAt, // Keep for reference
				isMainTourOperator: invitation.inviterUserId === 'SYSTEM' || invitation.inviterUserId === 'TRANSFERRED_OWNERSHIP'
			}))
		},
		deleteDialogTitle() {
			return this.deleteMode === 'user' ? this.$t('text.deleteUser') : this.$t('text.deleteInvitation')
		},
		deleteDialogMessage() {
			if (this.deleteMode === 'user') {
				return this.$t('text.deleteUserConfirmMessage', { 
					name: `${this.userToDelete?.firstName || ''} ${this.userToDelete?.lastName || ''}`.trim() || this.userToDelete?.email 
				})
			} else {
				return this.$t('text.deleteInvitationConfirmMessage', { 
					email: this.userToDelete?.email 
				})
			}
		},
		isMainTourOperator() {
			// Check if current user is the main user (no mainUserId field)
			const user = this.userIsImpersonating ? this.$store.state.selectedServiceProvider?.fields?.userAccount?.de : this.$store.state.loggedInUser
			return !user?.fields?.mainUserId?.de
		},

	},
	methods: {
			// beginLoading()/endLoading() replace direct `this.loading = true/false` writes.
			// mounted() calls hasUnlockedTradehubApps(), which itself awaits
			// getServiceProviderId() and loadInvitations() - each of those used to flip
			// `loading` true/false independently, so a single mount could toggle it
			// true -> false -> true -> false within milliseconds. That rapid re-trigger
			// interrupted the loading overlay's fade <transition> before its leave-cleanup
			// (which sets display:none) could finish, leaving the overlay stuck visible
			// forever even though `loading` had correctly settled back to false (MYS-7293
			// follow-up). Counting depth means only the outermost call actually flips the
			// visible state, so nested calls no longer cause extra toggles.
			beginLoading() {
				this.loadingDepth++
				this.loading = true
			},
			endLoading() {
				this.loadingDepth = Math.max(0, this.loadingDepth - 1)
				if (this.loadingDepth === 0) this.loading = false
			},
			async openAddUser() {
			// Ensure serviceProviderId is loaded before opening dialog
			if (!this.serviceProviderId) {
				console.log('Loading service provider ID...')
				await this.getServiceProviderId()
			}
			
			if (!this.serviceProviderId) {
				console.error('Failed to load service provider ID')
				this.errorTitle = this.$t('text.error')
				this.errorDetail = 'Unable to determine service provider ID'
				return
			}
			
			console.log('Opening add user dialog with serviceProviderId:', this.serviceProviderId)
			this.dialogMode = 'add'
			this.dialogUser = { firstName: '', lastName: '', email: '', language: 'en', userRole: 'User' }
			this.$nextTick(() => this.$refs.userDialog.open())
		},
		editUser(item) {
			this.dialogMode = 'edit'
			// Map the item data to the dialog format, preserving invitationId
			this.dialogUser = {
				firstName: item.firstName || '',
				lastName: item.lastName || '',
				email: item.email,
				language: item.preferredLocale, // Use the original locale code
				userRole: item.userRole,
				invitationId: item.invitationId,
				status: item.status
			}
			this.$nextTick(() => this.$refs.userDialog.open())
		},
		askDeleteInvitation(item) {
			this.userToDelete = item
			this.deleteMode = 'invitation'
			this.showDelete = true
		},
		askDeleteUser(item) {
			this.userToDelete = item
			this.deleteMode = 'user'
			this.showDelete = true
		},
		async getServiceProviderId() {
			if (this.serviceProviderId) return
			this.beginLoading()
			try {
				const selectedProvider = this.$store.state.selectedServiceProvider
				const loggedInUser = this.$store.state.loggedInUser
				const selectedProviderMainUserId = selectedProvider?.fields?.userAccount?.de?.fields?.mainUserId?.de
				const loggedInUserMainUserId = loggedInUser?.fields?.mainUserId?.de

				// Case 1: Trade hub user (no service provider) but has mainUserId
				if (loggedInUser?.fields?.type?.de === UserType.TRADE_HUB && loggedInUserMainUserId) {
					try {
						console.log('case 1:trade hub user case - user exists on its own without a service provider', loggedInUserMainUserId)
						const res = await this.$httpGet(`/service-provider-id?userId=${loggedInUserMainUserId}`)
						this.serviceProviderId = res.serviceProviderId
					} catch (e) {
						this.showError(e)
					}
					return
				}

				// Case 2: Service provider with mainUserId (invited service provider user) - should become obsolete if we decide to move on with only user entities without SPs for the invited users from the MyOrg app
				if (selectedProviderMainUserId) {
					try {
						console.log('case 2: service provider with mainUserId (invited service provider user)', selectedProviderMainUserId)
						const res = await this.$httpGet(`/service-provider-id?userId=${selectedProviderMainUserId}`)
						this.serviceProviderId = res.serviceProviderId
					} catch (e) {
						this.showError(e)
					}
					return
				}

				// Case 3: Main self-registered Tour Operator (no mainUserId)
				console.log('case 3: main self-registered Tour Operator (no mainUserId)', selectedProvider?.sys?.id)
				this.serviceProviderId = selectedProvider?.sys?.id
			} finally {
				// Always clears loading, even when a case above returns early or throws
				// outside its own try/catch - previously an early return (or an uncaught
				// throw) here left `loading` stuck true and the overlay spinning forever.
				this.endLoading()
			}
		},
		async confirmDelete() {
			try {
				const executive = new InvitationExecutive(this)
				
				if (this.deleteMode === 'user') {
					// Delete the complete user (MyServices + PEAK + invitation)
					const result = await executive.deleteUser(this.userToDelete.invitationId)
					if (!result.success) {
						throw new Error(result.errors?.join(', ') || 'Failed to delete user')
					}
				} else {
					// Just delete the invitation
					await executive.deleteInvitation(this.userToDelete.invitationId)
				}
				
				await this.loadInvitations()
				this.showDelete = false
				this.userToDelete = null
				this.deleteMode = 'invitation'
			} catch (error) {
				console.error('Failed to delete:', error)
				this.errorTitle = this.$t('text.error')
				this.errorDetail = error.message || this.$t('text.somethingWentWrong')
			}
		},
		async handleUserSaved(user) {
			console.log('User saved:', user)
			await this.loadInvitations()
		},
		async handleUserUpdated(user) {
			console.log('User updated:', user)
			await this.loadInvitations()
		},
		roleChipColor(role) {
			if (role === InvitationRole.ADMIN) return '#00AEEF'
			if (role === InvitationRole.USER) return 'purple'
			return '#9E9E9E'
		},
		statusChipColor(status) {
			if (status === 'SENT') return '#FF9800' // Orange
			if (status === 'ACCEPTED') return '#4CAF50' // Green
			if (status === 'EXPIRED') return '#F44336' // Red
			if (status === 'CANCELLED') return '#9E9E9E' // Grey
			if (status === 'MYS_SUCCESS_BUT_PEAK_FAILED') return '#FF9800' // Orange
			return '#2196F3' // Blue for DRAFT or unknown
		},
		statusDisplayText(status) {
			const statusMap = {
				'DRAFT': this.$t('text.draft'),
				'SENT': this.$t('text.invitationSent'),
				'ACCEPTED': this.$t('text.registered'),
				'EXPIRED': this.$t('text.expired'),
				'CANCELLED': this.$t('text.cancelled'),
				'MYS_SUCCESS_BUT_PEAK_FAILED': this.$t('text.mysSuccessButPeakFailed')
			}
			return statusMap[status] || status
		},
		localeToLanguageLabel(locale) {
			const localeMap = {
				'en': 'English',
				'de': 'Deutsch',
				'fr': 'Français',
				'it': 'Italiano',
				'nl': 'Nederlands'
			}
			return localeMap[locale] || 'English'
		},
		getComputedStatus(invitation) {
			// If already accepted, cancelled, or explicitly expired, return database status
			if (invitation.status === 'ACCEPTED' || 
				invitation.status === 'CANCELLED' ||
				invitation.status === 'MYS_SUCCESS_BUT_PEAK_FAILED' ||
				invitation.status === 'EXPIRED') {
				return invitation.status
			}

			// Check if expired based on expiry date
			if (invitation.expiresAt && new Date(invitation.expiresAt).getTime() < Date.now()) {
				return 'EXPIRED'
			}

			// Return database status for non-expired invitations
			return invitation.status
		},
		getOptionsForItem(item) {
			// Disable all interactions for the main tour operator
			if (item.isMainTourOperator) {
				return [{ label: 'text.edit', icon: 'mdi-pencil', action: 'edit' }]
			}

			const baseOptions = []
			
			// For pending invitations (SENT/EXPIRED)
			if (item.status === 'SENT' || item.status === 'EXPIRED') {
				baseOptions.push({ label: 'text.resendInvitation', icon: 'mdi-email-sync', action: 'resend' })
			}
			
			baseOptions.push({ label: 'text.edit', icon: 'mdi-pencil', action: 'edit' })
			
			// Cancel is only for sent invitations
			if (item.status === 'SENT') {
				baseOptions.push({ label: 'text.cancel', icon: 'mdi-cancel', action: 'cancel' })
			}

			// Change role option - only for registered users (ACCEPTED status)
			if (item.status === 'ACCEPTED' || item.status === 'MYS_SUCCESS_BUT_PEAK_FAILED') {
				// Determine the label based on current role
				const isAdmin = item.userRole === InvitationRole.ADMIN
				const label = isAdmin ? 'text.changeToUser' : 'text.changeToAdmin'
				
				baseOptions.push({
					label: label,
					icon: 'mdi-account-convert', 
					action: 'changeRole',
				})
			}

			// Transfer ownership option - only for the main TO who has signed the contrac
			if (this.canTransferOwnership(item)) {
				baseOptions.push({ 
					label: 'text.transferOwnership', 
					icon: 'mdi-account-switch', 
					action: 'transferOwnership',
					color: 'primary'
				})
			}
			
			// Delete logic: different actions for different statuses
			if (item.status === 'ACCEPTED' || item.status === 'MYS_SUCCESS_BUT_PEAK_FAILED') {
				// For registered users: Full user deletion
				baseOptions.push({ 
					label: 'text.deleteUser', 
					icon: 'mdi-account-remove', 
					action: 'deleteUser',
					color: 'error'
				})
			} else {
				// For non-registered users: Just delete the invitation
				baseOptions.push({ 
					label: 'text.deleteInvitation', 
					icon: 'mdi-delete', 
					action: 'deleteInvitation' 
				})
			}
			
			return baseOptions
		},
		async loadInvitations() {
			this.beginLoading()
			try {
				const executive = new InvitationExecutive(this)
				const invitations = await executive.getInvitations(this.clientId, this.serviceProviderId)
				// Force reactivity by creating a new array reference
				this.invitations = [...invitations]
			} catch (error) {
				console.error('Failed to load invitations:', error)
				this.errorTitle = this.$t('text.error')
				this.errorDetail = error.message || this.$t('text.somethingWentWrong')
			} finally {
				this.endLoading()
			}
		},
		async resendInvitation(item) {
			this.beginLoading()
			try {
				const executive = new InvitationExecutive(this)
				await executive.resendInvitation(item.invitationId)
				await this.loadInvitations() // Reload to get updated status
			} catch (error) {
				console.error('Failed to resend invitation:', error)
				this.errorTitle = this.$t('text.error')
				this.errorDetail = error.message || this.$t('text.somethingWentWrong')
			} finally {
				this.endLoading()
			}
		},
		async cancelInvitation(item) {
			this.beginLoading()
			try {
				const executive = new InvitationExecutive(this)
				await executive.cancelInvitation(item.invitationId)
				await this.loadInvitations() // Reload to get updated status
			} catch (error) {
				console.error('Failed to cancel invitation:', error)
				this.errorTitle = this.$t('text.error')
				this.errorDetail = error.message || this.$t('text.somethingWentWrong')
			} finally {
				this.endLoading()
			}
		},
		handleOptionClick(action, item) {
			switch (action.action || action) {
				case 'edit':
					this.editUser(item)
					break
				case 'deleteInvitation':
					this.askDeleteInvitation(item)
					break
				case 'deleteUser':
					this.askDeleteUser(item)
					break
				case 'resend':
					this.resendInvitation(item)
					break
				case 'cancel':
					this.cancelInvitation(item)
					break
				case 'changeRole':
					{
						// Determine new role - fallback to calculating if not in actionData
						const newRole = item.userRole === InvitationRole.ADMIN ? InvitationRole.USER : InvitationRole.ADMIN
						this.askChangeRole(item, newRole)
					}
					break
				case 'transferOwnership':
					this.askTransferOwnership(item)
					break
				default:
					console.warn('Unknown action:', action)
			}
		},
		canTransferOwnership(item) {
			// Only show transfer ownership if:
			// 1. Current user is an operator impersonating the Tour Operator
			// 2. Target user has accepted invitation (status ACCEPTED)
			// 3. Target user has Admin role
			// 4. Target user is not the same as current user
			const loggedInUser = this.$store.state.loggedInUser
			const isOperator = loggedInUser?.fields?.type?.de === UserType.OPERATOR
			
			return isOperator && 
				   (item.status === 'ACCEPTED') &&
				   item.userRole === InvitationRole.ADMIN &&
				   item.email.toLowerCase() !== this.userEmail?.toLowerCase()
		},
		askChangeRole(item, newRole) {
			// Ensure newRole is valid and one of the enum values
			if (!newRole || (newRole !== InvitationRole.ADMIN && newRole !== InvitationRole.USER)) {
				console.error('Invalid role provided:', newRole)
				this.errorTitle = this.$t('text.error')
				this.errorDetail = 'Invalid role specified'
				return
			}

			this.roleChangeTargetUser = item
			this.targetRole = newRole
			this.showChangeRole = true
		},
		async confirmChangeRole() {
			try {
				this.beginLoading()
				const executive = new InvitationExecutive(this)
				
				await executive.updateInvitationRole(this.roleChangeTargetUser.invitationId, this.targetRole)
				
				// Reload invitations to get the updated role
				await this.loadInvitations()
				
				// Wait for Vue to update the DOM
				await this.$nextTick()
				
				this.showChangeRole = false
				this.roleChangeTargetUser = null
				this.targetRole = null
			} catch (error) {
				console.error('Change role failed:', error)
				this.errorTitle = this.$t('text.error')
				this.errorDetail = error.message || this.$t('text.somethingWentWrong')
				this.showChangeRole = false
				this.roleChangeTargetUser = null
				this.targetRole = null
			} finally {
				this.endLoading()
			}
		},
		askTransferOwnership(item) {
			this.transferTargetUser = item
			this.showOwnershipTransfer = true
		},
		async confirmTransferOwnership() {
			try {
				this.beginLoading()
				const executive = new OwnershipTransferExecutive(this)
				
				const transferRequest = {
					currentMainUserEmail: this.userEmail,
					targetUserEmail: this.transferTargetUser.email,
					targetUserFirstName: this.transferTargetUser.firstName,
					targetUserLastName: this.transferTargetUser.lastName,
					clientId: this.clientId,
					serviceProviderId: this.serviceProviderId
				}

				const result = await executive.transferOrganizationOwnership(this.clientId, this.serviceProviderId, transferRequest)
				if (!result || typeof result.success !== 'boolean') {
					throw new Error(this.$t('text.ownershipTransferFailed'))
				}

				if (result.success) {
					const refreshUrl = this.userIsImpersonating
						? `/serviceprovider/${this.serviceProviderId}?startImpersonation=true`
						: `/serviceprovider/${this.serviceProviderId}`
					const refreshResponse = await this.$httpGet(refreshUrl)
					const refreshedServiceProvider = refreshResponse?.serviceProvider

					if (refreshedServiceProvider) {
						await this.$store.commit('setSelectedServiceProvider', refreshedServiceProvider)
						if (this.userIsImpersonating) {
							await this.$store.commit('setImpersonatedServiceProvider', refreshedServiceProvider)
							await this.$store.commit('setImpersonationContextToken', refreshResponse?.impersonationContextToken)
							if (refreshedServiceProvider.fields?.mainUserAccount?.fields?.applications?.de) {
								await this.$store.commit('setComponents', refreshedServiceProvider.fields.mainUserAccount.fields.applications.de)
							}
						}
					}

					this.showSuccessToast(this.$t('text.ownershipTransferSuccess'), true)
	
					this.$router.push(`/serviceprovider/${this.serviceProviderId}`)

					// Redirect to login since user's email has changed
					// setTimeout(() => {
					// 	this.$router.push('/login')
					// }, 2000)
				} else {
					const errorMessage = result.errors?.join(', ') || this.$t('text.ownershipTransferFailed')
					this.errorTitle = this.$t('text.error')
					this.errorDetail = errorMessage
					
					if (result.rollbackPerformed === false) {
						this.errorDetail += ' ' + this.$t('text.rollbackFailed')
					}
				}
				
				this.showOwnershipTransfer = false
				this.transferTargetUser = null
			} catch (error) {
				console.error('Transfer ownership failed:', error)
				this.errorTitle = this.$t('text.error')
				this.errorDetail = error.message || this.$t('text.somethingWentWrong')
				this.showOwnershipTransfer = false
				this.transferTargetUser = null
			} finally {
				this.endLoading()
			}
		},
		async hasUnlockedTradehubApps() {
			this.beginLoading()
			try {
				const tourOperatorExecutive = new TourOperatorExecutive(this)
				const serviceProviderId = this.serviceProviderId || this.$store.state.selectedServiceProvider?.sys?.id
				const hasUnlockedTradehubApps = await tourOperatorExecutive.hasUnlockedTradehubApps(this.userEmail, serviceProviderId)
				if (!hasUnlockedTradehubApps) {
					this.$router.push('/dashboard')
				} else {
					await this.getServiceProviderId()
					await this.loadInvitations()
				}
			} catch (error) {
				// Previously uncaught: an RPC failure here (e.g. on first page load,
				// before auth/store state is fully warmed up) left `loading` stuck true
				// forever with no error shown - just an infinite spinner.
				console.error('Failed to check tradehub app access:', error)
				this.errorTitle = this.$t('text.error')
				this.errorDetail = error.message || this.$t('text.somethingWentWrong')
			} finally {
				this.endLoading()
			}
		}
	},
	async mounted() {
		await this.hasUnlockedTradehubApps()
		
		// Set up periodic status refresh (every 30 seconds) to catch expiring invitations
		this.statusUpdateInterval = setInterval(() => {
			// Force reactivity update by reassigning the array
			this.invitations = [...this.invitations]
		}, 30000)
	},
	beforeUnmount() {
		// Clean up interval
		if (this.statusUpdateInterval) {
			clearInterval(this.statusUpdateInterval)
		}
	},
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/_common-form.scss';
.TradeHubMyOrg {
	.common-sidebar {
		:deep(.v-expansion-panel-title) {
			padding-top: 24px;
		}
	}
	.content-container {
		padding: 16px;
		margin-top: 60px;

		.FieldSet {
			&:first-of-type {
				.Field {
					padding-top: 24px;
					margin-bottom: 0px;
				}
			}
			&:last-of-type {
				padding-top: 24px;
			}
		}
		.my-org-container {
			&__empty {
				display: flex;
				> p {
					flex: 1;
					color: #8F8F8F;
					font-size: 15px;
					font-style: normal;
					font-weight: 400;
					line-height: 20px;
					letter-spacing: -0.037px;
					display: flex;
					align-items: center;
					padding-left: 12px;
				}
				
				> button {
					margin: 12px;
					margin-left: auto;
				}
			}
			&__table {
				width: 100%;
				border-collapse: collapse;
				> thead {
					border-bottom: 1px solid #e5e5e5;
					> th {
						padding: 14px 12px;
						font-size: 15px;
						font-weight: 500;
						line-height: 20px;
						letter-spacing: -0.037px;
						text-align: left;
						&:nth-child(1) {
							padding-left: 26px
						}
					}
				}
				> tbody {
					> tr {
						border-bottom: 1px solid #e5e5e5;
					}
					> tr:not(.expanded-row) {
						height: 60px;
						&.open > td:first-child > div > i::before {
							transform: rotate(90deg);
						}
						> td {
							padding: 0px 12px;
							&:first-child {
								padding-left: 0px;
								> div {
									display: flex;
									> p {
										padding-left: 3px;
									}
									> i::before {
										margin-top: -3px;
										transition: transform 0.2s ease-in-out;
									}
								}
							}
							&:last-child {
								width: 100px;
								> div {
									display: flex;
									justify-content: space-between;
								}
							}
							p {
								color: #000;
								font-size: 15px;
								font-weight: 400;
								line-height: 20px;
								letter-spacing: -0.037px;
							}
						}
						&:hover {
							background-color: #f9f9f9;
							cursor: pointer;
						}
					}
				}
			}
		}
	}
}

.card {
	background: white;
	border-radius: 8px;
	border: 1px solid #e5e5e5;
	padding: 12px;
}
.card__header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 12px 8px 8px 8px;
}
.card__title .title {
	font-size: 16px;
	font-weight: 600;
}
.card__title .subtitle {
	margin-top: 4px;
	color: #8F8F8F;
	font-size: 13px;
}

.dotsIcon {
	margin-right: 5px;

	&:hover:not(:disabled) {
		transform: scale(1.2);
	}
	
	&:disabled {
		opacity: 0.3;
		cursor: not-allowed;
	}
}

</style>
